import { ReactNode, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";

import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { tabPanelClasses } from "@mui/lab/TabPanel";
import {
  badgeClasses,
  Badge,
  Box,
  BoxProps,
  Tab,
  Typography,
  styled,
  tabClasses,
  tabsClasses,
} from "@mui/material";

import {
  FireworksIcon,
  PlayingIcon,
  SilhouetteIcon,
  TargetIcon,
  AnchorIcon,
  CommentIcon,
} from "@robin/common";

import ChartTab from "./ChartTab";
import ExperiencesTab from "./ExperiencesTab";
import GoalsTab from "./GoalsTab";
import TimelineTab from "./TimelineTab";
import { DateTime } from "./primitives/DateTime";
import { Patient } from "../models/patient";
import { TabValueType, TabValues } from "./Enums";
import { useAppContext } from "../context/AppContext";
import { usePatientData } from "../hooks/usePatientData";
import ActivitiesTab from "./ActivitiesTab";
import StrengthsTab from "./StrengthsTab";
import NotesTab from "./NotesTab";
import CrisisTab from "./CrisisTab";

const NAME = "Tabs";
const classes = {
  text: `${NAME}-text`,
};

const tabIcons: Record<TabValueType, ReactNode> = {
  chart: <SsidChartIcon />,
  timeline: <FormatListBulletedIcon />,
  experiences: <FireworksIcon />,
  goals: <TargetIcon />,
  activities: <PlayingIcon />,
  strengths: <SilhouetteIcon />,
  crisis: <AnchorIcon />,
  notes: <CommentIcon />,
};

const Root = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  padding: theme.spacing(0),
  position: "relative",
  [theme.breakpoints.down("md")]: {
    paddingBottom: theme.spacing(7.5),
  },
  [theme.breakpoints.down("sm")]: {
    paddingBottom: theme.spacing(7),
  },
  [`& .${tabsClasses.root}`]: {
    background: theme.palette.background.default,
    borderBottom: `0.75px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("md")]: {
      borderTop: `0.75px solid ${theme.palette.divider}`,
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 5,
    },
  },
  [`& .${tabClasses.root}`]: {
    justifyContent: "center",
    alignItems: "center",
    minHeight: theme.spacing(7),
    minWidth: "auto",
    [theme.breakpoints.up("md")]: {
      minHeight: theme.spacing(7.5),
    },
    [theme.breakpoints.up("xl")]: {
      minHeight: theme.spacing(8),
    },
  },
  [`& .${badgeClasses.badge}`]: {
    bottom: 0,
    fontSize: theme.typography.pxToRem(12),
    height: theme.spacing(2),
    left: "100%",
    minWidth: theme.spacing(2),
    padding: theme.spacing(0.25),
    right: "auto",
    top: "auto",
    transform: "translate(-0%, 0%)",
  },
  [`& .${tabClasses.root} .${classes.text}`]: {
    ...theme.typography.body2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("lg")]: {
      display: "none",
    },
  },
  [`& .${tabPanelClasses.root}`]: {
    flex: "1 1 auto",
    padding: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(3),
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.up("lg")]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.up("xl")]: {
      padding: theme.spacing(5),
    },
  },
}));

const Panels = styled(Box)(() => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  justifyContent: "space-between",
  overflow: "auto",
  scrollbarWidth: "thin",
  scrollBehavior: "smooth",
}));

const Footnotes = styled(Typography)(({ theme }) => ({
  alignItems: "center",
  color: theme.palette.text.disabled,
  display: "flex",
  gap: theme.spacing(0.75),
  justifyContent: "center",
  padding: theme.spacing(2),
}));

export const Tabs: React.FC<BoxProps & { patient: Patient }> = ({
  patient,
  ...boxProps
}) => {
  const { t } = useTranslation();
  const { dataConfig, tab, setTab } = useAppContext();

  const patientData = usePatientData({
    patientId: patient.id,
    exportId: dataConfig.exportId ?? undefined,
  });

  const handleChange = (_: React.SyntheticEvent, newValue: TabValueType) =>
    setTab(newValue);

  const counts = useMemo(
    () => ({
      experiences: patientData?.data.experiences?.length ?? undefined,
      goals: patientData?.data.weeklyTargets?.length ?? undefined,
      activities: patientData?.data.positiveActivitiesLog?.length ?? undefined,
      strengths: patientData?.data.strengths?.length ?? undefined,
      notes: patientData?.data.notes?.length ?? undefined,
    }),
    [patientData],
  );

  if (!patientData) return null;

  return (
    <TabContext value={tab}>
      <Root {...boxProps}>
        <TabList
          allowScrollButtonsMobile
          aria-label="lab API tabs example"
          onChange={handleChange}
          sx={{ flex: "0 0 auto" }}
          variant="fullWidth"
        >
          {Object.keys(TabValues).map((key) => {
            if (key === TabValues.timeline || key === TabValues.chart)
              return (
                <Tab
                  icon={tabIcons[key as TabValueType] as ReactElement}
                  iconPosition="start"
                  key={key}
                  label={
                    <span className={classes.text}>
                      {t(`tabs.${key}.tab-name`)}
                    </span>
                  }
                  sx={({ palette, breakpoints }) => ({
                    borderRight:
                      key === TabValues.timeline
                        ? `0.75px solid ${palette.divider}`
                        : "none",
                    [breakpoints.up("md")]: { flex: "0 0 20%" },
                  })}
                  value={key}
                />
              );
          })}
          {Object.keys(TabValues).map((key) => {
            if (key === TabValues.timeline || key === TabValues.chart)
              return null;
            const tabKey = key as keyof typeof counts;
            return (
              <Tab
                icon={
                  <Badge badgeContent={counts[tabKey]}>
                    <span>{tabIcons[key as TabValueType] as ReactElement}</span>
                  </Badge>
                }
                iconPosition="start"
                key={key}
                sx={({ breakpoints }) => ({
                  [breakpoints.up("md")]: { flex: "1 1 auto" },
                })}
                value={key}
              />
            );
          })}
        </TabList>
        <Panels>
          <ChartTab patient={patient} value={TabValues.chart} />
          <TimelineTab patient={patient} value={TabValues.timeline} />
          <ExperiencesTab data={patientData} value={TabValues.experiences} />
          <ActivitiesTab data={patientData} value={TabValues.activities} />
          <GoalsTab patient={patient} value={TabValues.goals} />
          <StrengthsTab data={patientData} value={TabValues.strengths} />
          <NotesTab data={patientData} value={TabValues.notes} />
          <CrisisTab data={patientData} value={TabValues.crisis} />
          <Footnotes variant="caption">
            {t("patient.export-date")}
            <DateTime timestamp={patientData.exportDate.getTime()}></DateTime>
          </Footnotes>
        </Panels>
      </Root>
    </TabContext>
  );
};

export default Tabs;
